import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Upload, Plus, X, Image, DollarSign, Shield,
    Globe, Users, Star, Check, AlertCircle, Edit
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Suppress specific error about findDOMNode
const originalError = console.error;
console.error = (...args) => {
    if (typeof args[0] === 'string' && args[0].includes('findDOMNode')) {
        return;
    }
    originalError.call(console, ...args);
};

// ==================== SIMPLE FORM COMPONENT ====================
const SimpleAppForm = ({ appId, isEditMode }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingApp, setLoadingApp] = useState(false);
    const [userRoles, setUserRoles] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});

    const [form, setForm] = useState({
        name: '',
        slug: '',
        short_description: '',
        dashboard_url: '',
        external_link: '',
        description: '',
        icon_image: null,
        banner_image: null,
        version: '',
        support: '',
        company: '',
        website: '',
        privacy_policy: '',
        payment_plan: '',
        type: '0', // 0=Free, 1=Premium, 2=In-App purchase
        monthly: '',
        yearly: '',
        user_role: [], // Multiple select
        status: '1', // 0=Inactive, 1=Active
    });

    // Rich text editor configuration (similar to Summernote)
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean']
        ],
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'align',
        'link', 'image'
    ];

    // Fetch user roles only (no categories)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const rolesRes = await api.get('/api/user-roles');
                setUserRoles(rolesRes.data || [
                    { id: 4, name: 'dentist' },
                    { id: 3, name: 'hygienist' },
                    { id: 35, name: 'dental_professional' },
                    { id: 36, name: 'developer_software_company' }
                ]);
            } catch (error) {
                console.error('Failed to fetch user roles:', error);
            }
        };
        fetchData();
    }, []);

    // Load app data if editing
    useEffect(() => {
        if (isEditMode && appId) {
            loadAppData();
        }
    }, [isEditMode, appId]);

    const loadAppData = async () => {
        setLoadingApp(true);
        try {
            const response = await api.get(`/api/apps/${appId}`);
            const app = response.data;

            // Populate form with existing internal app data
            // Map fields correctly for internal apps (not marketplace apps)
            setForm({
                name: app.name || '',
                slug: app.slug || '',
                short_description: app.short_description || app.tagline || '',
                dashboard_url: app.dashboard_url || '',
                external_link: app.external_link || app.website_url || '',
                description: app.description || app.long_description || '',
                icon_image: null, // Can't pre-populate file inputs
                banner_image: null,
                version: app.version || '1.0.0',
                support: app.support || app.support_url || '',
                company: app.company || app.company_name || '',
                website: app.website || app.website_url || '',
                privacy_policy: app.privacy_policy || app.privacy_policy_url || '',
                payment_plan: app.payment_plan || '',
                type: String(app.type ?? 0), // 0=Free, 1=Premium, 2=In-App
                monthly: app.monthly || app.price || '',
                yearly: app.yearly || '',
                user_role: app.user_role || [],
                status: String(app.status ?? 1), // 0=Inactive, 1=Active
            });
        } catch (error) {
            console.error('Failed to load app data:', error);
            alert('Failed to load app data. Please try again.');
            navigate('/dashboard/apps');
        } finally {
            setLoadingApp(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        const errors = {};
        
        if (!form.name.trim()) {
            errors.name = 'Name is required';
        }
        if (!form.short_description.trim()) {
            errors.short_description = 'Short Description is required';
        }
        if (!form.description.trim() || getPlainTextLength(form.description) === 0) {
            errors.description = 'Description is required';
        }
        if (!isEditMode && !form.icon_image) {
            errors.icon_image = 'Addon Icon is required';
        }
        if (!isEditMode && !form.banner_image) {
            errors.banner_image = 'Banner Image is required';
        }
        if (!form.version.trim()) {
            errors.version = 'Version is required';
        }
        if (!form.support.trim()) {
            errors.support = 'Contact Support is required';
        }
        if (!form.company.trim()) {
            errors.company = 'Company is required';
        }
        if (!form.website.trim()) {
            errors.website = 'Website is required';
        }
        if (!form.privacy_policy.trim()) {
            errors.privacy_policy = 'Privacy Policy is required';
        }
        if (form.user_role.length === 0) {
            errors.user_role = 'User Role is required';
        }
        if ((form.type === '1' || form.type === '2') && !form.monthly) {
            errors.monthly = 'Monthly Price is required';
        }
        if ((form.type === '1' || form.type === '2') && !form.yearly) {
            errors.yearly = 'Yearly Price is required';
        }
        
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            // Scroll to first error
            const firstErrorField = document.querySelector('.validation-error');
            if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }
        
        setValidationErrors({});
        setLoading(true);

        try {
            const formData = new FormData();

            // Append all text fields
            Object.keys(form).forEach(key => {
                if (key === 'user_role') {
                    // Handle array
                    formData.append(key, JSON.stringify(form[key]));
                } else if (key === 'icon_image' || key === 'banner_image') {
                    // Handle files
                    if (form[key]) {
                        formData.append(key, form[key]);
                    }
                } else if (key === 'type' || key === 'status' || key === 'payment_plan') {
                    // Convert to integer for these fields
                    formData.append(key, parseInt(form[key]) || 0);
                } else if (key === 'monthly' || key === 'yearly') {
                    // Convert to float for price fields
                    formData.append(key, parseFloat(form[key]) || 0);
                } else {
                    formData.append(key, form[key] || '');
                }
            });

            if (isEditMode && appId) {
                // Update existing app using POST request to /update endpoint
                await api.post(`/api/internal-apps/${appId}/update`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                alert('App updated successfully!');
            } else {
                // Create new app
                await api.post('/api/internal-apps', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                alert('App created successfully!');
            }
            navigate('/dashboard/apps');
        } catch (err) {
            alert(`Failed to ${isEditMode ? 'update' : 'create'} app: ${err.response?.data?.detail || err.message}`);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get plain text length from HTML content
    const getPlainTextLength = (html) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return (tempDiv.textContent || tempDiv.innerText || '').length;
    };

    const handleDescriptionChange = (content) => {
        const plainTextLength = getPlainTextLength(content);
        if (plainTextLength <= 500) {
            setForm({ ...form, description: content });
        }
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === 'file') {
            setForm({ ...form, [name]: files[0] });
        } else if (name === 'name') {
            // Limit name to 50 characters and auto-generate slug
            const limitedValue = value.slice(0, 50);
            const slug = limitedValue.toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
            setForm({ ...form, name: limitedValue, slug });
        } else if (name === 'short_description') {
            // Limit short description to 250 characters
            const limitedValue = value.slice(0, 250);
            setForm({ ...form, short_description: limitedValue });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleMultiSelect = (e) => {
        const options = e.target.options;
        const selected = [];
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) {
                selected.push(options[i].value);
            }
        }
        setForm({ ...form, user_role: selected });
    };

    if (loadingApp) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading app data...</span>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold">
                    {isEditMode ? 'Edit Internal App' : 'Add New Internal App'}
                </h1>
                <button
                    onClick={() => navigate('/dashboard/apps')}
                    className="text-gray-600 hover:text-gray-800"
                >
                    ← Back to My Apps
                </button>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-blue-800">
                    <strong>Internal Apps:</strong> Legacy system integration with webhooks and APIs.
                    Configure webhooks after creation to connect with legacy systems.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Name
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            maxLength={50}
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.name ? 'border-red-500' : ''}`}
                        />
                        {validationErrors.name && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.name}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            {form.name.length}/50 characters
                            {form.slug && <span className="ml-2">| Slug: {form.slug}</span>}
                        </p>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Short Description
                        </label>
                        <input
                            type="text"
                            name="short_description"
                            value={form.short_description}
                            onChange={handleChange}
                            maxLength={250}
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.short_description ? 'border-red-500' : ''}`}
                            placeholder="Brief one-line description"
                        />
                        {validationErrors.short_description && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.short_description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            {form.short_description.length}/250 characters
                        </p>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block mb-1 font-semibold">Dashboard URL</label>
                        <input
                            type="url"
                            name="dashboard_url"
                            value={form.dashboard_url}
                            onChange={handleChange}
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                            placeholder="https://dashboard.yourapp.com"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block mb-1 font-semibold">External Link</label>
                        <input
                            type="url"
                            name="external_link"
                            value={form.external_link}
                            onChange={handleChange}
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                            placeholder="https://external.yourapp.com"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Description
                        </label>
                        <ReactQuill
                            theme="snow"
                            value={form.description}
                            onChange={handleDescriptionChange}
                            modules={quillModules}
                            formats={quillFormats}
                            className={`bg-white ${validationErrors.description ? 'border border-red-500 rounded' : ''}`}
                            placeholder="Detailed description of the app..."
                        />
                        {validationErrors.description && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            {getPlainTextLength(form.description)}/500 characters
                        </p>
                    </div>
                </div>

                {/* Images */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Addon Icon
                        </label>
                        <input
                            type="file"
                            name="icon_image"
                            onChange={handleChange}
                            accept="image/png, image/jpeg"
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.icon_image ? 'border-red-500' : ''}`}
                        />
                        {validationErrors.icon_image && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.icon_image}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">PNG or JPEG format</p>
                    </div>

                    <div>
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Banner Image
                        </label>
                        <input
                            type="file"
                            name="banner_image"
                            onChange={handleChange}
                            accept="image/png, image/jpeg"
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.banner_image ? 'border-red-500' : ''}`}
                        />
                        {validationErrors.banner_image && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.banner_image}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">PNG or JPEG format</p>
                    </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Version
                        </label>
                        <input
                            type="text"
                            name="version"
                            value={form.version}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.version ? 'border-red-500' : ''}`}
                            placeholder="1.0.0"
                        />
                        {validationErrors.version && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.version}</p>
                        )}
                    </div>

                    <div>
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Contact Support
                        </label>
                        <input
                            type="text"
                            name="support"
                            value={form.support}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.support ? 'border-red-500' : ''}`}
                            placeholder="support@company.com"
                        />
                        {validationErrors.support && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.support}</p>
                        )}
                    </div>

                    <div>
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Company
                        </label>
                        <input
                            type="text"
                            name="company"
                            value={form.company}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.company ? 'border-red-500' : ''}`}
                            placeholder="Company Name"
                        />
                        {validationErrors.company && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.company}</p>
                        )}
                    </div>
                </div>

                {/* Website & Privacy */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Website
                        </label>
                        <input
                            type="url"
                            name="website"
                            value={form.website}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.website ? 'border-red-500' : ''}`}
                            placeholder="https://company.com"
                        />
                        {validationErrors.website && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.website}</p>
                        )}
                    </div>

                    <div>
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Privacy Policy
                        </label>
                        <input
                            type="url"
                            name="privacy_policy"
                            value={form.privacy_policy}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.privacy_policy ? 'border-red-500' : ''}`}
                            placeholder="https://company.com/privacy"
                        />
                        {validationErrors.privacy_policy && (
                            <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.privacy_policy}</p>
                        )}
                    </div>
                </div>

                {/* Pricing */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-1 font-semibold">Payment Plan</label>
                        <select
                            name="payment_plan"
                            value={form.payment_plan}
                            onChange={handleChange}
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select</option>
                            <option value="1">Payment plan</option>
                        </select>
                    </div>

                    <div>
                        <label className="block mb-1 font-semibold">
                            <span className="text-red-500">*</span> Type
                        </label>
                        <select
                            name="type"
                            value={form.type}
                            onChange={handleChange}
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="0">Free</option>
                            <option value="1">Premium</option>
                            <option value="2">In-App purchase</option>
                        </select>
                    </div>
                </div>

                {/* Premium/In-App Pricing (show for both Premium and In-App purchase) */}
                {(form.type === '1' || form.type === '2') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                        <div>
                            <label className="block mb-1 font-semibold">
                                <span className="text-red-500">*</span> Monthly Price ($)
                            </label>
                            <input
                                type="number"
                                name="monthly"
                                value={form.monthly}
                                onChange={handleChange}
                                className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.monthly ? 'border-red-500' : ''}`}
                                min="0"
                                step="0.01"
                                placeholder="9.99"
                            />
                            {validationErrors.monthly && (
                                <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.monthly}</p>
                            )}
                        </div>

                        <div>
                            <label className="block mb-1 font-semibold">
                                <span className="text-red-500">*</span> Yearly Price ($)
                            </label>
                            <input
                                type="number"
                                name="yearly"
                                value={form.yearly}
                                onChange={handleChange}
                                className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.yearly ? 'border-red-500' : ''}`}
                                min="0"
                                step="0.01"
                                placeholder="99.99"
                            />
                            {validationErrors.yearly && (
                                <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.yearly}</p>
                            )}
                        </div>
                    </div>
                )}

                {/* User Roles */}
                <div>
                    <label className="block mb-1 font-semibold">
                        <span className="text-red-500">*</span> User Role
                    </label>
                    <select
                        name="user_role"
                        multiple
                        value={form.user_role}
                        onChange={handleMultiSelect}
                        className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.user_role ? 'border-red-500' : ''}`}
                        style={{ height: '120px' }}
                    >
                        {userRoles.map(role => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                    </select>
                    {validationErrors.user_role && (
                        <p className="text-xs text-red-500 mt-1 validation-error">{validationErrors.user_role}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Hold Ctrl (Cmd on Mac) to select multiple</p>
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-3 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                    {loading ? 'Processing...' : (isEditMode ? 'Update Internal App' : 'Create Internal App')}
                </button>
            </form>

            {isEditMode && (
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Next Steps</h3>
                    <p className="text-sm text-gray-600 mb-3">
                        After updating, configure webhooks in My Apps to connect with legacy systems.
                    </p>
                    <button
                        onClick={() => navigate('/dashboard/apps')}
                        className="text-blue-600 hover:underline text-sm"
                    >
                        ← Back to My Apps
                    </button>
                </div>
            )}
        </div>
    );
};

// ==================== MARKETPLACE FORM COMPONENT ====================
const MarketplaceForm = ({ appId, isEditMode }) => {
    const navigate = useNavigate();
    const MIN_FOUNDED_YEAR = 1900;
    const MAX_FOUNDED_YEAR = new Date().getFullYear();
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [currentStep, setCurrentStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [loadingApp, setLoadingApp] = useState(false);
    const [uploadingScreenshots, setUploadingScreenshots] = useState(false);
    const [screenshotFiles, setScreenshotFiles] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [termsAccepted, setTermsAccepted] = useState(false);

    const [form, setForm] = useState({
        // Basic Info
        name: '',
        tagline: '',
        short_description: '',
        long_description: '',
        category: '',
        category_id: null,
        subcategory_id: null,
        tags: [],

        // URLs & Technical
        website_url: 'https://',
        demo_url: '',
        documentation_url: '',
        support_url: '',
        privacy_policy_url: '',
        terms_url: '',

        // Visuals
        icon_url: 'https://',
        logo_url: '',
        screenshots: [],
        demo_video_url: '',

        // Features & Benefits
        key_features: ['', '', ''],
        use_cases: ['', ''],
        integrations: [],

        // Pricing
        pricing_model: 'free',
        price: 0,
        price_period: 'monthly',
        free_trial_days: 0,
        refund_policy: 'https://',

        // Business Info
        company_name: '',
        company_size: '',
        founded_year: '',
        target_audience: '',

        // Marketing
        unique_selling_points: ['', '', ''],
        competitor_comparison: '',
        customer_testimonials: [],

        // Licensing (add these)
        licensing_type: 'manual',
        license_keys: [],
        universal_access_code: '',
        licensing_instructions: '',
        webhook_license_url: ''
    });

    const pricingModels = [
        { value: 'free', label: 'Free', icon: '🎁' },
        { value: 'freemium', label: 'Freemium', icon: '💎' },
        { value: 'one_time', label: 'One-time Purchase', icon: '💳' },
        { value: 'subscription', label: 'Subscription', icon: '🔄' },
        { value: 'usage_based', label: 'Usage-based', icon: '📊' }
    ];

    const steps = [
        { number: 1, title: 'Basic Information', icon: '📝' },
        { number: 2, title: 'App Assets', icon: '🖼️' },
        { number: 3, title: 'Features & Benefits', icon: '⭐' },
        { number: 4, title: 'Pricing & Business', icon: '💰' },
        { number: 5, title: 'Licensing Setup', icon: '🔑' },
        { number: 6, title: 'Review & Submit', icon: isEditMode ? '💾' : '🚀' }
    ];

    // Fetch categories (store all data including subcategories)
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/api/categories');
                // console.log('Fetched categories:', response.data);

                // API returns only parent categories with nested subcategories
                // No need to filter - response.data is already parent categories
                setCategories(response.data);

                if (response.data.length > 0 && !form.category_id && !isEditMode) {
                    setForm(prev => ({
                        ...prev,
                        category: response.data[0].name,
                        category_id: response.data[0].id
                    }));
                    // Set initial subcategories if first category has them
                    if (response.data[0].subcategories && response.data[0].subcategories.length > 0) {
                        setSubcategories(response.data[0].subcategories);
                    } else {
                        setSubcategories([]);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch categories:', error);
            }
        };
        fetchCategories();
    }, []);


    // Update subcategories when category changes
    useEffect(() => {
        if (form.category_id && categories.length > 0) {
            // Find the selected category from the stored categories
            const selectedCategory = categories.find(cat => cat.id === form.category_id);
            // console.log('Selected category:', selectedCategory);
            // console.log('Subcategories:', selectedCategory?.subcategories);

            if (selectedCategory && selectedCategory.subcategories && selectedCategory.subcategories.length > 0) {
                setSubcategories(selectedCategory.subcategories);
            } else {
                setSubcategories([]);
            }
        } else {
            setSubcategories([]);
        }
    }, [form.category_id, categories]);

    // Load app data if editing
    useEffect(() => {
        if (isEditMode && appId) {
            loadAppData();
        }
    }, [isEditMode, appId]);

    const loadAppData = async () => {
        setLoadingApp(true);
        try {
            const response = await api.get(`/api/apps/${appId}`);
            const app = response.data;

            // Populate form with existing app data
            setForm({
                name: app.name || '',
                tagline: app.tagline || '',
                short_description: app.short_description || app.description || '',
                long_description: app.long_description || '',
                category: app.category || '',
                category_id: app.category_id || null,
                subcategory_id: app.subcategory_id || null,
                tags: app.tags ? app.tags.split(',').map(tag => tag.trim()) : [],
                website_url: app.website || '',
                demo_url: app.demo_url || '',
                documentation_url: app.documentation_url || '',
                support_url: app.support_url || '',
                privacy_policy_url: app.privacy_policy_url || '',
                terms_url: app.terms_url || '',
                icon_url: app.icon_url || '',
                logo_url: app.logo_url || '',
                screenshots: app.screenshots || [],
                demo_video_url: app.demo_video_url || '',
                key_features: app.key_features || ['', '', ''],
                use_cases: app.use_cases || ['', ''],
                integrations: app.integrations || [],
                pricing_model: app.pricing_model || 'free',
                price: app.price || 0,
                price_period: app.price_period || 'monthly',
                free_trial_days: app.free_trial_days || 0,
                refund_policy: app.refund_policy || '',
                company_name: app.company_name || '',
                company_size: app.company_size || '',
                founded_year: app.founded_year || '',
                target_audience: app.target_audience || '',
                unique_selling_points: app.unique_selling_points || ['', '', ''],
                competitor_comparison: app.competitor_comparison || '',
                customer_testimonials: app.customer_testimonials || [],

                // Add licensing fields
                licensing_type: app.licensing_type || 'manual',
                license_keys: app.license_keys || [],
                universal_access_code: app.universal_access_code || '',
                licensing_instructions: app.licensing_instructions || '',
                webhook_license_url: app.webhook_license_url || ''
            });
        } catch (error) {
            console.error('Failed to load app data:', error);
            alert('Failed to load app data. Please try again.');
            navigate('/dashboard/apps');
        } finally {
            setLoadingApp(false);
        }
    };


    const handleChange = (field, value) => {
        setForm({ ...form, [field]: value });
    };

    // Helper function to ensure URL fields maintain https:// prefix
    const handleUrlChange = (field, value) => {
        // If user deletes everything or tries to remove https://, restore it
        if (!value || !value.startsWith('https://')) {
            value = 'https://';
        }
        setForm({ ...form, [field]: value });
    };

    const handleArrayChange = (field, index, value) => {
        const newArray = [...form[field]];
        newArray[index] = value;
        setForm({ ...form, [field]: newArray });
    };

    const addArrayItem = (field, defaultValue = '') => {
        setForm({ ...form, [field]: [...form[field], defaultValue] });
    };

    const removeArrayItem = (field, index) => {
        const newArray = form[field].filter((_, i) => i !== index);
        setForm({ ...form, [field]: newArray });
    };

    const handleTagInput = (e) => {
        if (e.key === 'Enter' && e.target.value.trim()) {
            e.preventDefault();
            if (!form.tags.includes(e.target.value.trim())) {
                setForm({ ...form, tags: [...form.tags, e.target.value.trim()] });
            }
            e.target.value = '';
        }
    };

    const removeTag = (index) => {
        setForm({ ...form, tags: form.tags.filter((_, i) => i !== index) });
    };

    // Enhanced validation function with detailed error messages
    const getValidationErrors = (step) => {
        const errors = {};

        switch (step) {
            case 1:
                if (!form.name || form.name.trim().length === 0) {
                    errors.name = 'App name is required';
                }
                if (!form.tagline || form.tagline.trim().length === 0) {
                    errors.tagline = 'Tagline is required';
                }
                if (!form.short_description || form.short_description.trim().length === 0) {
                    errors.short_description = 'Short description is required';
                }
                if (!form.category) {
                    errors.category = 'Please select a category';
                }
                break;

            case 2:
                if (!form.website_url || form.website_url.trim().length === 0 || form.website_url === 'https://') {
                    errors.website_url = 'Website URL is required';
                } else if (!isValidUrl(form.website_url)) {
                    errors.website_url = 'Please enter a valid URL';
                }
                if (!form.icon_url || form.icon_url.trim().length === 0 || form.icon_url === 'https://') {
                    errors.icon_url = 'App icon is required';
                } else if (!isValidUrl(form.icon_url)) {
                    errors.icon_url = 'Please enter a valid icon URL';
                }
                if (form.demo_url && !isValidUrl(form.demo_url)) {
                    errors.demo_url = 'Please enter a valid demo URL';
                }
                if (form.documentation_url && !isValidUrl(form.documentation_url)) {
                    errors.documentation_url = 'Please enter a valid documentation URL';
                }
                if (form.support_url && !isValidUrl(form.support_url)) {
                    errors.support_url = 'Please enter a valid support URL';
                }
                break;

            case 3:
                const filledFeatures = form.key_features.filter(f => f && f.trim().length > 0);
                if (filledFeatures.length < 2) {
                    errors.key_features = 'Please add at least 2 key features';
                }
                break;

            case 4:
                if (!form.company_name || form.company_name.trim().length === 0) {
                    errors.company_name = 'Company name is required';
                }
                if (form.founded_year !== '' && form.founded_year !== null && form.founded_year !== undefined) {
                    const foundedYear = String(form.founded_year).trim();

                    if (!/^\d{4}$/.test(foundedYear)) {
                        errors.founded_year = 'Founded year must be a 4-digit year';
                    } else {
                        const parsedYear = parseInt(foundedYear, 10);
                        if (parsedYear < MIN_FOUNDED_YEAR || parsedYear > MAX_FOUNDED_YEAR) {
                            errors.founded_year = `Founded year must be between ${MIN_FOUNDED_YEAR} and ${MAX_FOUNDED_YEAR}`;
                        }
                    }
                }
                if (form.pricing_model === 'one_time' || form.pricing_model === 'subscription') {
                    if (!form.price || parseFloat(form.price) <= 0) {
                        errors.price = 'Price must be greater than 0 for paid models';
                    }
                }
                if (form.pricing_model === 'subscription' && !form.price_period) {
                    errors.price_period = 'Billing period is required for subscriptions';
                }
                break;

            case 5:
                if (form.licensing_type === 'manual') {
                    if (!form.licensing_instructions || form.licensing_instructions.trim().length === 0) {
                        errors.licensing_instructions = 'Please provide licensing instructions';
                    }
                } else if (form.licensing_type === 'key_pool') {
                    if (!form.license_keys || form.license_keys.length === 0) {
                        errors.license_keys = 'Please add at least one license key';
                    }
                } else if (form.licensing_type === 'universal_code') {
                    if (!form.universal_access_code || form.universal_access_code.trim().length === 0) {
                        errors.universal_access_code = 'Please provide a universal access code';
                    }
                } else if (form.licensing_type === 'webhook') {
                    if (!form.webhook_license_url || form.webhook_license_url.trim().length === 0) {
                        errors.webhook_license_url = 'Webhook URL is required';
                    } else if (!isValidUrl(form.webhook_license_url)) {
                        errors.webhook_license_url = 'Please enter a valid webhook URL';
                    }
                }
                break;

            case 6:
                const allErrors = {
                    ...getValidationErrors(1),
                    ...getValidationErrors(2),
                    ...getValidationErrors(3),
                    ...getValidationErrors(4),
                    ...getValidationErrors(5)
                };
                return allErrors;
        }

        return errors;
    };

    // URL validation helper
    const isValidUrl = (url) => {
        try {
            new URL(url);
            return true;
        } catch (e) {
            return false;
        }
    };

    // Simple validation check (returns boolean)
    const validateStep = (step) => {
        const errors = getValidationErrors(step);
        return Object.keys(errors).length === 0;
    };

    // Handle step navigation with validation
    const handleNextStep = () => {
        const errors = getValidationErrors(currentStep);

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            // Scroll to top to show error message
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setValidationErrors({});
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Automatically clear validation errors when all required fields are filled
    useEffect(() => {
        // Only check if there are existing validation errors
        if (Object.keys(validationErrors).length > 0) {
            const errors = getValidationErrors(currentStep);
            // If no errors remain, clear the validation errors state
            if (Object.keys(errors).length === 0) {
                setValidationErrors({});
            }
        }
    }, [form, currentStep]);

    // Handle direct step navigation (clicking on step icons)
    const handleStepClick = (stepNumber) => {
        // Allow navigation to completed steps or current step
        if (stepNumber <= currentStep) {
            setCurrentStep(stepNumber);
            setValidationErrors({});
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Validate current step before allowing forward navigation
        const errors = getValidationErrors(currentStep);
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Validate all steps between current and target
        for (let i = currentStep; i < stepNumber; i++) {
            const stepErrors = getValidationErrors(i);
            if (Object.keys(stepErrors).length > 0) {
                setValidationErrors(stepErrors);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        setValidationErrors({});
        setCurrentStep(stepNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Enhanced submit with final validation
    const handleSubmit = async () => {
        const errors = getValidationErrors(6);

        // Check if terms are accepted
        if (!termsAccepted) {
            errors.terms = 'You must accept the terms and conditions to submit';
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setSubmitting(true);
        try {
            const submitData = {
                name: form.name,
                tagline: form.tagline,
                short_description: form.short_description,
                long_description: form.long_description || '',
                category: form.category,
                category_id: form.category_id,
                subcategory_id: form.subcategory_id,
                tags: form.tags.join(','),
                website_url: form.website_url,
                icon_url: form.icon_url,
                demo_url: form.demo_url || '',
                documentation_url: form.documentation_url || '',
                support_url: form.support_url || '',
                key_features: form.key_features.filter(f => f),
                pricing_model: form.pricing_model,
                price: parseFloat(form.price) || 0,
                company_name: form.company_name,

                licensing_type: form.licensing_type || 'manual',
                license_keys: form.licensing_type === 'key_pool' ? form.license_keys : [],
                universal_access_code: form.licensing_type === 'universal_code' ? form.universal_access_code : '',
                licensing_instructions: form.licensing_instructions || '',
                webhook_license_url: form.licensing_type === 'webhook' ? form.webhook_license_url : '',

                free_trial_days: form.free_trial_days ? parseInt(form.free_trial_days) : null,
                founded_year: form.founded_year ? form.founded_year.toString() : null,

                metadata: {
                    screenshots: form.screenshots.filter(s => s),
                    use_cases: form.use_cases.filter(u => u),
                    integrations: form.integrations,
                    price_period: form.price_period,
                    refund_policy: form.refund_policy,
                    company_size: form.company_size,
                    target_audience: form.target_audience,
                    unique_selling_points: form.unique_selling_points.filter(u => u),
                    privacy_policy_url: form.privacy_policy_url,
                    terms_url: form.terms_url,
                    logo_url: form.logo_url,
                    demo_video_url: form.demo_video_url
                }
            };

            if (isEditMode && appId) {
                await api.put(`/api/apps/${appId}/update`, submitData);
                // Success - redirect without alert
                navigate('/dashboard/apps');
            } else {
                await api.post('/api/apps/submit', submitData);
                // Success - redirect without alert
                navigate('/dashboard/apps');
            }
        } catch (err) {
            setValidationErrors({ submit: err.response?.data?.detail || 'Failed to submit app. Please try again.' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleScreenshotUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        if (form.screenshots.length + files.length > 10) {
            alert('Maximum of 10 screenshots allowed');
            return;
        }

        setUploadingScreenshots(true);

        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file);
            });

            const response = await api.post('/api/upload/screenshots', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                params: { app_id: appId }
            });

            // Use the direct S3 URL from the response
            const newScreenshots = [...form.screenshots];
            response.data.images.forEach(img => {
                newScreenshots.push(img.url); // This is now the direct S3 URL
            });

            setForm({ ...form, screenshots: newScreenshots });

        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload screenshots');
        } finally {
            setUploadingScreenshots(false);
        }
    };

    // Image component that uses our API
    const SecureImage = ({ src, alt, className }) => {
        // Since our API serves the images, we can use them directly
        return <img src={src} alt={alt} className={className} />;
    };


    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
                {isEditMode && <Edit className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />}
                <h1 className="text-2xl sm:text-3xl font-bold">
                    {isEditMode ? 'Edit Your App' : 'Submit Your App to Dentulu Marketplace'}
                </h1>
            </div>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                {isEditMode ? 'Update your app details and marketplace information' : 'Join thousands of dental practices using innovative apps'}
            </p>

            {/* Progress Steps - Responsive */}
            <div className="mb-6 sm:mb-8">
                {/* Mobile: Vertical Stack */}
                <div className="flex sm:hidden flex-col gap-3">
                    {steps.map((step) => (
                        <div
                            key={step.number}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer
                            ${currentStep >= step.number
                                    ? 'bg-blue-50 border-blue-600'
                                    : 'bg-white border-gray-300'}
                            ${currentStep === step.number ? 'ring-2 ring-blue-200' : ''}`}
                            onClick={() => handleStepClick(step.number)}
                        >
                            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 flex-shrink-0
                            ${currentStep >= step.number
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-500'}`}
                            >
                                <span className="text-lg">{step.icon}</span>
                            </div>
                            <div className="flex-1">
                                <p className={`font-medium text-sm ${currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'}`}>
                                    Step {step.number}: {step.title}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Desktop: Horizontal Progress Bar */}
                <div className="hidden sm:flex items-center justify-between">
                    {steps.map((step, index) => (
                        <div key={step.number} className="flex items-center flex-1">
                            <div className="flex flex-col items-center flex-1">
                                <div
                                    className={`flex items-center justify-center w-10 h-10 lg:w-12 lg:h-12 rounded-full border-2 transition-all cursor-pointer
                                    ${currentStep >= step.number
                                            ? 'bg-blue-600 border-blue-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-500'}
                                    ${currentStep === step.number ? 'ring-4 ring-blue-200' : ''}
                                    hover:scale-110`}
                                    onClick={() => handleStepClick(step.number)}
                                    title={`Go to ${step.title}`}
                                >
                                    <span className="text-lg lg:text-xl">{step.icon}</span>
                                </div>
                                <p className={`mt-2 text-xs lg:text-sm font-medium text-center max-w-[100px] ${currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'}`}>
                                    {step.title}
                                </p>
                            </div>
                            {index < steps.length - 1 && (
                                <div className={`h-1 flex-1 mx-2 ${currentStep > step.number ? 'bg-blue-600' : 'bg-gray-300'}`} />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Show validation errors at the top if any - but NOT for step 6 (Review & Submit) */}
            {Object.keys(validationErrors).length > 0 && currentStep !== 6 && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                            <h3 className="font-semibold text-red-800 text-sm sm:text-base">To continue, all mandatory information must be provided in the designated fields.</h3>
                            <ul className="list-disc list-inside mt-2 text-red-700 text-sm">
                                {Object.entries(validationErrors).map(([field, error]) => (
                                    <li key={field}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow">
                {/* Step 1: Basic Information */}
                {currentStep === 1 && (
                    <div className="space-y-4 sm:space-y-6">
                        <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Tell us about your app</h2>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">
                                App Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => {
                                    handleChange('name', e.target.value);
                                    if (validationErrors.name) {
                                        setValidationErrors({ ...validationErrors, name: undefined });
                                    }
                                }}
                                className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${validationErrors.name ? 'border-red-500' : ''
                                    }`}
                                placeholder="My Awesome Dental App"
                            />
                            {validationErrors.name && (
                                <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.name}</p>
                            )}
                        </div>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">
                                Tagline <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.tagline}
                                onChange={(e) => {
                                    handleChange('tagline', e.target.value);
                                    if (validationErrors.tagline) {
                                        setValidationErrors({ ...validationErrors, tagline: undefined });
                                    }
                                }}
                                className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${validationErrors.tagline ? 'border-red-500' : ''
                                    }`}
                                placeholder="The smartest way to manage your dental practice"
                                maxLength={60}
                            />
                            <p className="text-xs sm:text-sm text-gray-500 mt-1">{form.tagline.length}/60 characters</p>
                            {validationErrors.tagline && (
                                <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.tagline}</p>
                            )}
                        </div>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">
                                Short Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={form.short_description}
                                onChange={(e) => {
                                    handleChange('short_description', e.target.value);
                                    if (validationErrors.short_description) {
                                        setValidationErrors({ ...validationErrors, short_description: undefined });
                                    }
                                }}
                                className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${validationErrors.short_description ? 'border-red-500' : ''
                                    }`}
                                rows="3"
                                placeholder="Brief description that appears in search results (150 characters)"
                                maxLength={150}
                            />
                            <p className="text-xs sm:text-sm text-gray-500 mt-1">{form.short_description.length}/150 characters</p>
                            {validationErrors.short_description && (
                                <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.short_description}</p>
                            )}
                        </div>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">Detailed Description</label>
                            <textarea
                                value={form.long_description}
                                onChange={(e) => handleChange('long_description', e.target.value)}
                                className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                rows="6"
                                placeholder="Provide a comprehensive description of your app, its benefits, and how it helps dental practices..."
                            />
                        </div>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">
                                Category <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={form.category_id || ''}
                                onChange={(e) => {
                                    const selectedId = e.target.value ? parseInt(e.target.value) : null;
                                    const selectedCat = categories.find(cat => cat.id === selectedId);

                                    // Update all fields at once to avoid race conditions
                                    setForm(prev => ({
                                        ...prev,
                                        category_id: selectedId,
                                        category: selectedCat ? selectedCat.name : '',
                                        subcategory_id: null
                                    }));

                                    if (validationErrors.category) {
                                        setValidationErrors({ ...validationErrors, category: undefined });
                                    }
                                }}
                                className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${validationErrors.category ? 'border-red-500' : ''
                                    }`}
                            >
                                <option value="">Select a category</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                            {validationErrors.category && (
                                <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.category}</p>
                            )}
                        </div>

                        {/* Subcategory selection - shown only if parent category has subcategories */}
                        {subcategories.length > 0 && (
                            <div>
                                <label className="block mb-2 font-medium text-sm sm:text-base">
                                    Subcategory (Optional)
                                </label>
                                <select
                                    value={form.subcategory_id || ''}
                                    onChange={(e) => {
                                        const selectedId = e.target.value ? parseInt(e.target.value) : null;
                                        handleChange('subcategory_id', selectedId);
                                    }}
                                    className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                >
                                    <option value="">None (General {form.category})</option>
                                    {subcategories.map(subcat => (
                                        <option key={subcat.id} value={subcat.id}>{subcat.name}</option>
                                    ))}
                                </select>
                                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                    Select a subcategory for better app discoverability
                                </p>
                            </div>
                        )}

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">Tags</label>
                            <div className="flex flex-wrap gap-2 mb-2">
                                {form.tags.map((tag, index) => (
                                    <span key={index} className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1 text-xs sm:text-sm">
                                        {tag}
                                        <X className="w-3 h-3 sm:w-4 sm:h-4 cursor-pointer" onClick={() => removeTag(index)} />
                                    </span>
                                ))}
                            </div>
                            <input
                                type="text"
                                onKeyDown={handleTagInput}
                                className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                placeholder="Type and press Enter to add tags"
                            />
                        </div>
                    </div>
                )}

                {/* Step 2: App Assets */}
                {currentStep === 2 && (
                    <div className="space-y-4 sm:space-y-6">
                        <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">App Assets & Links</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            <div>
                                <label className="block mb-2 font-medium text-sm sm:text-base">App Icon URL *</label>
                                <input
                                    type="url"
                                    value={form.icon_url}
                                    onChange={(e) => {
                                        handleUrlChange('icon_url', e.target.value);
                                        if (validationErrors.icon_url) {
                                            setValidationErrors({ ...validationErrors, icon_url: undefined });
                                        }
                                    }}
                                    className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${validationErrors.icon_url ? 'border-red-500' : ''
                                        }`}
                                    placeholder="https://example.com/icon.png"
                                />
                                <p className="text-xs sm:text-sm text-gray-500 mt-1">Square image, min 256x256px</p>
                                {validationErrors.icon_url && (
                                    <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.icon_url}</p>
                                )}
                            </div>

                            <div>
                                <label className="block mb-2 font-medium text-sm sm:text-base">App Logo URL</label>
                                <input
                                    type="url"
                                    value={form.logo_url}
                                    onChange={(e) => handleUrlChange('logo_url', e.target.value)}
                                    className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                    placeholder="https://example.com/logo.png"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">Screenshots</label>
                            <p className="text-xs sm:text-sm text-gray-600 mb-3">Upload up to 10 screenshots (max 5MB each)</p>

                            {/* Display uploaded screenshots - Responsive Grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                                {form.screenshots.map((url, index) => (
                                    <div key={index} className="relative group">
                                        <img
                                            src={url}
                                            alt={`Screenshot ${index + 1}`}
                                            className="w-full h-24 sm:h-32 object-cover rounded-lg bg-gray-200"
                                            onError={(e) => {
                                                if (!e.target.dataset.errorHandled) {
                                                    console.error('Failed to load image:', url);
                                                    e.target.dataset.errorHandled = 'true';
                                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2RkZCIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjE1MCIgeT0iMTAwIiBzdHlsZT0iZmlsbDojOTk5O2ZvbnQtd2VpZ2h0OmJvbGQ7Zm9udC1zaXplOjE2cHg7Zm9udC1mYW1pbHk6QXJpYWwsc2Fucy1zZXJpZiI+SW1hZ2UgRmFpbGVkPC90ZXh0Pjwvc3ZnPg==';
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newScreenshots = form.screenshots.filter((_, i) => i !== index);
                                                setForm({ ...form, screenshots: newScreenshots });
                                            }}
                                            className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-red-500 text-white rounded-full p-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-3 h-3 sm:w-4 sm:h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Upload button */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                                <label className="cursor-pointer">
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleScreenshotUpload}
                                        className="hidden"
                                        disabled={uploadingScreenshots}
                                    />
                                    <div className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm sm:text-base">
                                        <Upload className="w-4 h-4" />
                                        {uploadingScreenshots ? 'Uploading...' : 'Upload Screenshots'}
                                    </div>
                                </label>

                                <span className="text-xs sm:text-sm text-gray-500">
                                    {form.screenshots.length}/10 screenshots
                                </span>
                            </div>
                        </div>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">Demo Video URL</label>
                            <input
                                type="url"
                                value={form.demo_video_url}
                                onChange={(e) => handleUrlChange('demo_video_url', e.target.value)}
                                className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                placeholder="https://youtube.com/watch?v=..."
                            />
                            <p className="text-xs sm:text-sm text-gray-500 mt-1">YouTube or Vimeo URL</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            <div>
                                <label className="block mb-2 font-medium text-sm sm:text-base">Website URL *</label>
                                <input
                                    type="url"
                                    value={form.website_url}
                                    onChange={(e) => {
                                        handleUrlChange('website_url', e.target.value);
                                        if (validationErrors.website_url) {
                                            setValidationErrors({ ...validationErrors, website_url: undefined });
                                        }
                                    }}
                                    className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${validationErrors.website_url ? 'border-red-500' : ''
                                        }`}
                                    placeholder="https://yourapp.com"
                                />
                                {validationErrors.website_url && (
                                    <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.website_url}</p>
                                )}
                            </div>

                            <div>
                                <label className="block mb-2 font-medium text-sm sm:text-base">Demo URL</label>
                                <input
                                    type="url"
                                    value={form.demo_url}
                                    onChange={(e) => handleUrlChange('demo_url', e.target.value)}
                                    className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                    placeholder="https://demo.yourapp.com"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            <div>
                                <label className="block mb-2 font-medium text-sm sm:text-base">Documentation URL</label>
                                <input
                                    type="url"
                                    value={form.documentation_url}
                                    onChange={(e) => handleUrlChange('documentation_url', e.target.value)}
                                    className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                    placeholder="https://docs.yourapp.com"
                                />
                            </div>

                            <div>
                                <label className="block mb-2 font-medium text-sm sm:text-base">Support URL</label>
                                <input
                                    type="url"
                                    value={form.support_url}
                                    onChange={(e) => handleUrlChange('support_url', e.target.value)}
                                    className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                    placeholder="https://support.yourapp.com"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3: Features & Benefits */}
                {currentStep === 3 && (
                    <div className="space-y-4 sm:space-y-6">
                        <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Features & Benefits</h2>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">Key Features *</label>
                            <p className="text-xs sm:text-sm text-gray-600 mb-3">List at least 2 main features</p>
                            {form.key_features.map((feature, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={feature}
                                        onChange={(e) => {
                                            handleArrayChange('key_features', index, e.target.value);
                                            if (validationErrors.key_features) {
                                                setValidationErrors({ ...validationErrors, key_features: undefined });
                                            }
                                        }}
                                        className="flex-1 p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                        placeholder="e.g., Automated appointment reminders"
                                    />
                                    {index > 2 && (
                                        <button
                                            onClick={() => removeArrayItem('key_features', index)}
                                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                onClick={() => addArrayItem('key_features')}
                                className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                                <Plus className="w-4 h-4" /> Add Feature
                            </button>
                            {validationErrors.key_features && (
                                <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.key_features}</p>
                            )}
                        </div>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">Use Cases</label>
                            <p className="text-xs sm:text-sm text-gray-600 mb-3">How dental practices use your app</p>
                            {form.use_cases.map((useCase, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <textarea
                                        value={useCase}
                                        onChange={(e) => handleArrayChange('use_cases', index, e.target.value)}
                                        className="flex-1 p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                        rows="2"
                                        placeholder="Describe a specific use case..."
                                    />
                                    {index > 1 && (
                                        <button
                                            onClick={() => removeArrayItem('use_cases', index)}
                                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                onClick={() => addArrayItem('use_cases')}
                                className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                                <Plus className="w-4 h-4" /> Add Use Case
                            </button>
                        </div>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">Unique Selling Points</label>
                            <p className="text-xs sm:text-sm text-gray-600 mb-3">What makes your app stand out?</p>
                            {form.unique_selling_points.map((usp, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={usp}
                                        onChange={(e) => handleArrayChange('unique_selling_points', index, e.target.value)}
                                        className="flex-1 p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                        placeholder="e.g., Only app with real-time insurance verification"
                                    />
                                    {index > 2 && (
                                        <button
                                            onClick={() => removeArrayItem('unique_selling_points', index)}
                                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div>
                            <label className="block mb-2 font-medium text-sm sm:text-base">Target Audience</label>
                            <input
                                type="text"
                                value={form.target_audience}
                                onChange={(e) => handleChange('target_audience', e.target.value)}
                                className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                placeholder="e.g., Small to medium dental practices, DSOs, Specialists"
                            />
                        </div>
                    </div>
                )}

                {/* Step 4: Pricing & Business */}
                {currentStep === 4 && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-semibold mb-4 sm:mb-6">Pricing & Business Model</h2>

                        <div>
                            <label className="block mb-2 font-medium">Pricing Model</label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {pricingModels.map(model => (
                                    <label
                                        key={model.value}
                                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${form.pricing_model === model.value
                                            ? 'border-blue-600 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <input
                                            type="radio"
                                            name="pricing_model"
                                            value={model.value}
                                            checked={form.pricing_model === model.value}
                                            onChange={(e) => handleChange('pricing_model', e.target.value)}
                                            className="sr-only"
                                        />
                                        <div className="text-center">
                                            <span className="text-2xl">{model.icon}</span>
                                            <p className="font-medium mt-2">{model.label}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {(form.pricing_model !== 'free') && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block mb-1 font-semibold">Price</label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={form.price}
                                                onChange={(e) => {
                                                    let value = e.target.value;

                                                    // Remove $ or any non-numeric non-decimal characters
                                                    value = value.replace(/[^0-9.]/g, '');

                                                    // Prevent multiple decimal points
                                                    const parts = value.split('.');
                                                    if (parts.length > 2) {
                                                        value = parts[0] + '.' + parts.slice(1).join('');
                                                    }

                                                    // Limit to 2 decimal places
                                                    if (parts[1] && parts[1].length > 2) {
                                                        value = parts[0] + '.' + parts[1].substring(0, 2);
                                                    }

                                                    // Convert final cleaned string to a number
                                                    const numericValue = parseFloat(value) || 0;

                                                    // Keep raw value in input (so user can see what they typed)
                                                    handleChange('price', value);

                                                    // Clear validation error once corrected
                                                    if (validationErrors.price) {
                                                        setValidationErrors({ ...validationErrors, price: undefined });
                                                    }
                                                }}
                                                className={`w-full pl-10 p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.price ? 'border-red-500' : ''
                                                    }`}
                                                placeholder="0.00"
                                            />

                                        </div>
                                        {validationErrors.price && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.price}</p>
                                        )}
                                    </div>

                                    {form.pricing_model === 'subscription' && (
                                        <div>
                                            <label className="block mb-1 font-semibold">Billing Period</label>
                                            <select
                                                value={form.price_period}
                                                onChange={(e) => {
                                                    handleChange('price_period', e.target.value);
                                                    if (validationErrors.price_period) {
                                                        setValidationErrors({ ...validationErrors, price_period: undefined });
                                                    }
                                                }}
                                                className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.price_period ? 'border-red-500' : ''
                                                    }`}
                                            >
                                                <option value="monthly">Monthly</option>
                                                <option value="yearly">Yearly</option>
                                                <option value="per_user_monthly">Per User/Month</option>
                                                <option value="per_location">Per Location</option>
                                            </select>
                                            {validationErrors.price_period && (
                                                <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.price_period}</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block mb-1 font-semibold">Free Trial (days)</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={form.free_trial_days}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                const numValue = Math.min(Math.max(parseInt(value) || 0, 0), 365);
                                                handleChange('free_trial_days', numValue);
                                            }}
                                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                                            placeholder="14"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Maximum 365 days</p>
                                    </div>

                                    <div>
                                        <label className="block mb-1 font-semibold">Refund Policy URL</label>
                                        <input
                                            type="url"
                                            value={form.refund_policy}
                                            onChange={(e) => {
                                                handleUrlChange('refund_policy', e.target.value);
                                                if (validationErrors.refund_policy) {
                                                    setValidationErrors({ ...validationErrors, refund_policy: undefined });
                                                }
                                            }}
                                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${validationErrors.refund_policy ? 'border-red-500' : ''
                                                }`}
                                            placeholder="https://yourcompany.com/refund-policy"
                                        />
                                        {validationErrors.refund_policy && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.refund_policy}</p>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="border-t pt-6">
                            <h3 className="text-lg font-semibold mb-4 text-gray-900 bg-white">Company Information</h3>

                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block mb-2 font-medium text-gray-900 bg-white">Company Name *</label>
                                    <input
                                        type="text"
                                        value={form.company_name}
                                        onChange={(e) => {
                                            handleChange('company_name', e.target.value);
                                            if (validationErrors.company_name) {
                                                setValidationErrors({ ...validationErrors, company_name: undefined });
                                            }
                                        }}
                                        className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white${validationErrors.company_name ? 'border-red-500' : ''
                                            }`}
                                        placeholder="Your Company Inc."
                                    />
                                    {validationErrors.company_name && (
                                        <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.company_name}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block mb-2 font-medium text-gray-900 bg-white text-gray-900 bg-white">Company Size</label>
                                    <select
                                        value={form.company_size}
                                        onChange={(e) => handleChange('company_size', e.target.value)}
                                        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                                    >
                                        <option value="">Select size</option>
                                        <option value="1-10">1-10 employees</option>
                                        <option value="11-50">11-50 employees</option>
                                        <option value="51-200">51-200 employees</option>
                                        <option value="201-500">201-500 employees</option>
                                        <option value="500+">500+ employees</option>
                                    </select>
                                </div>
                            </div>

                            <div className="mt-4">
                                <label className="block mb-2 font-medium text-gray-900 bg-white">Founded Year</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={form.founded_year}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                                        handleChange('founded_year', value);
                                        if (validationErrors.founded_year) {
                                            setValidationErrors({ ...validationErrors, founded_year: undefined });
                                        }
                                    }}
                                    className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${validationErrors.founded_year ? 'border-red-500' : ''
                                        }`}
                                    placeholder="2020"
                                />
                                <p className="text-xs text-gray-500 mt-1">Enter a year between {MIN_FOUNDED_YEAR} and {MAX_FOUNDED_YEAR}</p>
                                {validationErrors.founded_year && (
                                    <p className="text-red-500 text-xs sm:text-sm mt-1">{validationErrors.founded_year}</p>
                                )}

                            </div>
                        </div>
                    </div>
                )}

                {/* Step 5: Licensing Setup */}
                {currentStep === 5 && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-semibold mb-4 sm:mb-6">Licensing & Access Setup</h2>

                        <div className="bg-blue-50 p-4 rounded-lg mb-6">
                            <p className="text-sm text-blue-800">
                                <strong>Choose how customers will receive access to your app after purchase.</strong>
                                <br />
                                Different licensing models suit different types of applications.
                            </p>
                        </div>

                        <div>
                            <label className="block mb-2 font-medium">
                                Licensing Type <span className="text-red-500">*</span>
                            </label>
                            <div className="space-y-3">
                                {/* Manual Provisioning */}
                                <label className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${form.licensing_type === 'manual'
                                    ? 'border-blue-600 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}>
                                    <input
                                        type="radio"
                                        name="licensing_type"
                                        value="manual"
                                        checked={form.licensing_type === 'manual'}
                                        onChange={(e) => handleChange('licensing_type', e.target.value)}
                                        className="sr-only"
                                    />
                                    <div className="flex items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl">👤</span>
                                                <h3 className="font-semibold text-lg">Manual Provisioning</h3>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-2">
                                                You'll manually send credentials/access to each customer after purchase.
                                                Best for custom setups or enterprise clients.
                                            </p>
                                        </div>
                                    </div>
                                </label>

                                {/* License Key Pool */}
                                <label className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${form.licensing_type === 'key_pool'
                                    ? 'border-blue-600 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}>
                                    <input
                                        type="radio"
                                        name="licensing_type"
                                        value="key_pool"
                                        checked={form.licensing_type === 'key_pool'}
                                        onChange={(e) => handleChange('licensing_type', e.target.value)}
                                        className="sr-only"
                                    />
                                    <div className="flex items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl">🔑</span>
                                                <h3 className="font-semibold text-lg">Pre-generated License Keys</h3>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-2">
                                                Upload a pool of license keys. System automatically assigns one to each customer.
                                                Keys are single-use and removed from pool after assignment.
                                            </p>
                                        </div>
                                    </div>
                                </label>

                                {/* Universal Code */}
                                <label className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${form.licensing_type === 'universal_code'
                                    ? 'border-blue-600 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}>
                                    <input
                                        type="radio"
                                        name="licensing_type"
                                        value="universal_code"
                                        checked={form.licensing_type === 'universal_code'}
                                        onChange={(e) => handleChange('licensing_type', e.target.value)}
                                        className="sr-only"
                                    />
                                    <div className="flex items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl">🎟️</span>
                                                <h3 className="font-semibold text-lg">Universal Access Code</h3>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-2">
                                                All customers receive the same code. Useful for discount codes, referral links,
                                                or apps with external license management.
                                            </p>
                                        </div>
                                    </div>
                                </label>

                                {/* API/Webhook Integration */}
                                <label className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${form.licensing_type === 'webhook'
                                    ? 'border-blue-600 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}>
                                    <input
                                        type="radio"
                                        name="licensing_type"
                                        value="webhook"
                                        checked={form.licensing_type === 'webhook'}
                                        onChange={(e) => handleChange('licensing_type', e.target.value)}
                                        className="sr-only"
                                    />
                                    <div className="flex items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl">🔗</span>
                                                <h3 className="font-semibold text-lg">API / Webhook Integration</h3>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-2">
                                                Connect to your licensing API. System calls your endpoint to generate licenses
                                                in real-time. Best for SaaS platforms with existing infrastructure.
                                            </p>
                                        </div>
                                    </div>
                                </label>
                            </div>
                            {validationErrors.licensing_type && (
                                <p className="text-red-500 text-sm mt-1">{validationErrors.licensing_type}</p>
                            )}
                        </div>

                        {/* Conditional fields based on licensing type */}
                        {form.licensing_type === 'manual' && (
                            <div>
                                <label className="block mb-2 font-medium">
                                    Setup Instructions for Customers <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={form.licensing_instructions}
                                    onChange={(e) => {
                                        handleChange('licensing_instructions', e.target.value);
                                        if (validationErrors.licensing_instructions) {
                                            setValidationErrors({ ...validationErrors, licensing_instructions: undefined });
                                        }
                                    }}
                                    className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${validationErrors.licensing_instructions ? 'border-red-500' : ''
                                        }`}
                                    rows="4"
                                    placeholder="E.g., You'll receive login credentials within 24 hours via email. Check your spam folder if you don't see our email."
                                    required
                                />
                                {validationErrors.licensing_instructions && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors.licensing_instructions}</p>
                                )}
                                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                    This message will be shown to customers after purchase
                                </p>
                            </div>
                        )}

                        {form.licensing_type === 'key_pool' && (
                            <div>
                                <label className="block mb-2 font-medium">
                                    License Keys (one per line) <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={form.license_keys.join('\n')}
                                    onChange={(e) => {
                                        const keys = e.target.value.split('\n').filter(k => k.trim());
                                        handleChange('license_keys', keys);
                                        if (validationErrors.license_keys) {
                                            setValidationErrors({ ...validationErrors, license_keys: undefined });
                                        }
                                    }}
                                    className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm ${validationErrors.license_keys ? 'border-red-500' : ''
                                        }`}
                                    rows="10"
                                    placeholder="ABC123-DEF456-GHI789&#10;JKL012-MNO345-PQR678&#10;..."
                                    required
                                />
                                {validationErrors.license_keys && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors.license_keys}</p>
                                )}
                                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                    Available keys: {form.license_keys.filter(k => k.trim()).length}
                                </p>
                            </div>
                        )}

                        {form.licensing_type === 'universal_code' && (
                            <>
                                <div>
                                    <label className="block mb-2 font-medium">
                                        Universal Access Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.universal_access_code}
                                        onChange={(e) => {
                                            handleChange('universal_access_code', e.target.value);
                                            if (validationErrors.universal_access_code) {
                                                setValidationErrors({ ...validationErrors, universal_access_code: undefined });
                                            }
                                        }}
                                        className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono ${validationErrors.universal_access_code ? 'border-red-500' : ''
                                            }`}
                                        placeholder="DENTULU2024"
                                        required
                                    />
                                    {validationErrors.universal_access_code && (
                                        <p className="text-red-500 text-sm mt-1">{validationErrors.universal_access_code}</p>
                                    )}
                                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                        All customers will receive this same code
                                    </p>
                                </div>

                                <div>
                                    <label className="block mb-2 font-medium">
                                        Instructions for Using the Code
                                    </label>
                                    <textarea
                                        value={form.licensing_instructions}
                                        onChange={(e) => handleChange('licensing_instructions', e.target.value)}
                                        className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        rows="3"
                                        placeholder="E.g., Enter this code at checkout on our website for 100% discount"
                                    />
                                </div>
                            </>
                        )}

                        {form.licensing_type === 'webhook' && (
                            <>
                                <div>
                                    <label className="block mb-2 font-medium">
                                        License Generation API Endpoint <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="url"
                                        value={form.webhook_license_url}
                                        onChange={(e) => {
                                            handleChange('webhook_license_url', e.target.value);
                                            if (validationErrors.webhook_license_url) {
                                                setValidationErrors({ ...validationErrors, webhook_license_url: undefined });
                                            }
                                        }}
                                        className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${validationErrors.webhook_license_url ? 'border-red-500' : ''
                                            }`}
                                        placeholder="https://api.yourapp.com/generate-license"
                                        required
                                    />
                                    {validationErrors.webhook_license_url && (
                                        <p className="text-red-500 text-sm mt-1">{validationErrors.webhook_license_url}</p>
                                    )}
                                    <p className="text-sm text-gray-500 mt-1">
                                        We'll POST purchase details and expect a license key in response
                                    </p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm font-medium mb-2">Expected API Format:</p>
                                    <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
                                        {`POST ${form.webhook_license_url || 'your-endpoint'}
{
  "purchase_id": "pur_123",
  "customer_email": "user@example.com",
  "customer_name": "John Doe",
  "app_id": "app_456",
  "timestamp": "2024-01-01T00:00:00Z"
}

Expected Response:
{
  "license_key": "XXXX-XXXX-XXXX-XXXX",
  "expires_at": "2025-01-01T00:00:00Z"
}`}
                                    </pre>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Step 6: Review & Submit - Keep existing implementation */}
                {currentStep === 6 && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-semibold mb-4 sm:mb-6">
                            {isEditMode ? 'Review Your Changes' : 'Review Your Submission'}
                        </h2>

                        <div className="bg-gray-50 p-6 rounded-lg">
                            <h3 className="font-semibold text-lg mb-4">App Overview</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-600">Name:</span>
                                    <p className="font-medium">{form.name}</p>
                                </div>
                                <div>
                                    <span className="text-gray-600">Category:</span>
                                    <p className="font-medium">{form.category}</p>
                                </div>
                                <div>
                                    <span className="text-gray-600">Pricing:</span>
                                    <p className="font-medium">
                                        {form.pricing_model === 'free' ? 'Free' : `$${form.price} ${form.price_period}`}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-gray-600">Company:</span>
                                    <p className="font-medium">{form.company_name}</p>
                                </div>
                                <div>
                                    <span className="text-gray-600">Licensing:</span>
                                    <p className="font-medium">
                                        {form.licensing_type === 'manual' ? 'Manual Provisioning' :
                                            form.licensing_type === 'key_pool' ? 'Pre-generated Keys' :
                                                form.licensing_type === 'universal_code' ? 'Universal Code' :
                                                    form.licensing_type === 'webhook' ? 'API Integration' : 'Not Set'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-50 p-6 rounded-lg">
                            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                <Shield className="w-5 h-5 text-blue-600" />
                                {isEditMode ? 'Update Guidelines' : 'Marketplace Guidelines'}
                            </h3>
                            <ul className="space-y-2 text-sm">
                                <li className="flex items-start gap-2">
                                    <Check className="w-4 h-4 text-green-600 mt-0.5" />
                                    <span>Your app must integrate with Dentulu's API ecosystem</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check className="w-4 h-4 text-green-600 mt-0.5" />
                                    <span>All data handling must comply with HIPAA regulations</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check className="w-4 h-4 text-green-600 mt-0.5" />
                                    <span>
                                        {isEditMode
                                            ? 'Updates are applied immediately'
                                            : 'Apps are reviewed within 3-5 business days'
                                        }
                                    </span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check className="w-4 h-4 text-green-600 mt-0.5" />
                                    <span>You'll receive email updates on your submission status</span>
                                </li>
                            </ul>
                        </div>

                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                id="terms"
                                checked={termsAccepted}
                                onChange={(e) => {
                                    setTermsAccepted(e.target.checked);
                                    if (validationErrors.terms) {
                                        setValidationErrors({ ...validationErrors, terms: undefined });
                                    }
                                }}
                                className={`mt-1 ${validationErrors.terms ? 'border-red-500 ring-2 ring-red-200' : ''
                                    }`}
                            />
                            <label htmlFor="terms" className="text-sm">
                                I confirm that all information provided is accurate and I agree to the{' '}
                                <a
                                    href="/terms"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                >
                                    Dentulu Marketplace Terms of Service
                                </a>
                                {' '}and Developer Agreement.
                            </label>
                        </div>
                        {validationErrors.terms && (
                            <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                {validationErrors.terms}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6">
                <button
                    onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                    disabled={currentStep === 1}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Previous
                </button>

                {currentStep < 6 ? (
                    <button
                        onClick={handleNextStep}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Next Step
                    </button>
                ) : (
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {submitting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                {isEditMode ? 'Updating...' : 'Submitting...'}
                            </>
                        ) : (
                            <>
                                <Check className="w-5 h-5" />
                                {isEditMode ? 'Update App' : 'Submit App for Review'}
                            </>
                        )}
                    </button>
                )}
            </div>
        </div >

    );
};

// ==================== MAIN COMPONENT ====================
export default function SubmitApp() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Extract parameters
    const mode = searchParams.get('mode');
    const appId = searchParams.get('appId');
    const isSimple = searchParams.get('simple') === 'true';

    // Determine form type
    const isEditMode = mode === 'edit';
    const isSimpleMode = mode === 'simple' || isSimple;

    // Show appropriate form
    if (isSimpleMode || (isEditMode && isSimple)) {
        return <SimpleAppForm appId={appId} isEditMode={isEditMode} />;
    }

    return <MarketplaceForm appId={appId} isEditMode={isEditMode} />;
}